import { zodResolver } from '@hookform/resolvers/zod';
import { AxiosError } from 'axios';
import { camelizeKeys } from 'humps';
import { omit } from 'lodash';
import posthog from 'posthog-js';
import { useEffect, useState } from 'react';
import {
  type FieldValues,
  FormProvider,
  type UseFormProps,
  useForm,
} from 'react-hook-form';
import type { z } from 'zod';
import { camelizeKey } from '../../../helpers';
import { useZodContext, ZodContext } from './contexts/zodContext';
import { FormError } from './errors';
import { useInLocalStorageTTL } from './hooks/useInLocalStorageTTL';
import { useLocalStorageTTL } from './hooks/useLocalStorageTTL';
import { RestoreFormDataModal } from './RestoreFormDataModal';
import { FORM_ERROR } from './types';

/**
 * FormSchema
 * @public
 * This type is used to define the schema for the form.
 */
export type FormSchema<T extends z.Schema<any, any>> = {
  /**
   * Validation schema from Zod for form validation.
   */
  schema?: T;

  /**
   * Return data to submit only when matching with schema.
   */
  strictData?: boolean;
};

/**
 * FormProps
 * @public
 */
export type FormProps<T extends FieldValues = any, R = any> = UseFormProps<T> &
  FormSchema<any> & {
    /**
     * Cache key for form
     * When provided, the form will be cached in local storage before submit form. After submit, cache will be removed.
     */
    cacheKey?: string;

    /**
     * Cache time in milliseconds
     */
    cacheTime?: number;

    /**
     * Cache exclude fields
     */
    cacheExclude?: string[];

    /**
     * Function to call when form is submitted
     * @param data - form data
     */
    onSubmit: (data: T) => R | Promise<R>;

    /**
     * Function to call when form is submitted with error
     * @param error
     * @param data
     */
    onError?: (error: any, data: T) => void;

    /**
     * Function to call when form is submitted successfully
     * @param data
     */
    onSuccess?: (data: T) => void;

    /**
     * Form children
     */
    children?: React.ReactNode;

    /**
     * Render function
     * @param form
     */
    render?: (form: ReturnType<typeof useForm>) => React.ReactNode;

    /**
     * Form hook to override default form hook inside in Form component
     */
    form?: ReturnType<typeof useForm<any, any>>;

    /**
     * Style for form
     */
    style?: React.CSSProperties;
  };

/**
 * Form component
 *
 * @example
 * ```tsx
 * <Form onSubmit={onSubmit}>
 *   <FormError />
 *   <TextField name="name" />
 *   <FormButton type="submit">Submit</Button>
 * </Form>
 * ```
 *
 * @example
 * ```tsx
 * const form = useForm();
 *
 * return (
 *  <Form form={form} onSubmit={onSubmit}>
 *    <FormError />
 *    <TextField name="name" />
 *    <FormButton type="submit">Submit</Button>
 *  </Form>
 * );
 * ```
 *
 * @public
 * @param cacheKey - cache key for form
 * @param cacheTime - cache time in milliseconds
 * @param cacheExclude - cache exclude fields
 * @param onSubmit - function to call when form is submitted
 * @param onError - function to call when form is submitted with error
 * @param onSuccess - function to call when form is submitted successfully
 * @param children - form children
 * @param render - render function
 * @param form - form hook
 * @param formProps - form props
 * @param style - form style
 * @param schema - validation schema
 * @param strictData - return data to submit only when matching with schema
 *
 * @see FormProps
 * @see useForm
 * @see FormError
 * @see FormButton
 */
export const Form: React.FC<FormProps> = ({
  cacheKey,
  cacheTime,
  cacheExclude = [],
  onSubmit,
  onSuccess,
  onError,
  children,
  render,
  form,
  style,
  schema,
  strictData,
  ...formProps
}) => {
  const [cache, setCache, removeCache] = useLocalStorageTTL(
    cacheKey,
    cacheTime,
    form ? form.watch() : formProps.defaultValues,
  );
  const inCache = useInLocalStorageTTL(cacheKey);
  const [openRestore, setOpenRestore] = useState<boolean>(inCache);

  const schemaResolver = schema ? { resolver: zodResolver(schema) } : {};
  const insideController = useForm({ ...schemaResolver, ...formProps });
  const insideForm = form || insideController;
  const insideFormData = insideForm.watch();

  /**
   * Set cache when form is dirty
   */
  useEffect(() => {
    if (cacheKey) {
      if (insideForm.formState.isDirty) {
        setCache(omit(insideFormData, cacheExclude));
      }
    } else {
      removeCache();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cacheKey, JSON.stringify(insideFormData)]);

  /**
   * Restore form data from cache
   */
  const restoreDataFromCache = () => {
    insideForm.reset(cache);
    posthog.capture('FormRestoreDataFromCache');
    setOpenRestore(false);
  };

  const cleanedDataBySchema = (data: unknown) => {
    if (strictData && schema) {
      const result = schema.safeParse(data);
      return result.data;
    }
    return data;
  };

  /**
   * Handle axios error
   * @param error
   */
  const handleAxiosError = (error: any) => {
    if (error instanceof AxiosError) {
      const { response } = error || {};
      const data: any = response?.data || {};
      const status = response?.status || 0;

      if (status >= 400) {
        /**
         * Set a general error message when not provided other errors
         */
        if (data?.message && !data?.errors) {
          insideForm.setError(FORM_ERROR, {
            message: data.message,
          });
        }

        const uuidRegex =
          /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

        /**
         * Change key to camelCase except uuid (save uuid in original format)
         */
        const errors = camelizeKeys(data.errors || {}, (key, convert) =>
          key.split('.').reduce((acc, item) => {
            const newKey = uuidRegex.test(item)
              ? item
              : camelizeKey(item, convert);

            return [acc, newKey].join('.');
          }, ''),
        );

        /**
         * Set field errors
         */
        Object.entries(errors).forEach(([key, value]: [string, any]) => {
          insideForm.setError(key, {
            type: 'api',
            message: value[0],
          });
        });
      }
    }
  };

  /**
   * Handle form error
   * @param error
   */
  const handleFormError = (error: any) => {
    if (error instanceof FormError) {
      insideForm.setError(error.key, {
        type: error.type || 'validation',
        message: error.message,
      });
    }
  };

  /**
   * Overwrite onSubmit function
   * @param formData
   */
  const overwriteOnSubmit = async (formData: unknown) => {
    try {
      const dataToSubmit = cleanedDataBySchema(formData);

      await onSubmit(dataToSubmit);
      onSuccess?.(dataToSubmit);
      removeCache();
    } catch (error: any) {
      handleAxiosError(error);
      handleFormError(error);
      onError?.(error, formData);
    }
  };

  // Get the zod context with the schema
  const zodContext = useZodContext(schema);

  return (
    <>
      <RestoreFormDataModal
        opened={openRestore}
        onRestore={() => restoreDataFromCache()}
        onClose={() => setOpenRestore(false)}
      />
      <FormProvider {...insideForm}>
        <ZodContext.Provider value={zodContext}>
          <form
            onSubmit={ev => {
              ev.stopPropagation();
              insideController.clearErrors();
              return insideForm.handleSubmit(overwriteOnSubmit)(ev);
            }}
            style={style}
          >
            {render?.(insideForm) || children}
          </form>
        </ZodContext.Provider>
      </FormProvider>
    </>
  );
};
